import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { analytics } from '@/analytics';
import Download from '@/assets/icons/download.svg?component';
import { Button, type ButtonSize, type ButtonVariant } from '@/components/atoms/Buttons';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { ToastError } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { sanitizeFileName } from '@/utils/fileNameFormatting';
import { saveBlob } from '@/utils/saveBlob';

import { loadReactPdf } from './loadReactPdf';
import { PDF_PALETTE, type PdfPalette } from './pdfPalette';

interface DownloadPdfProps {
    buttonText: string;
    variant?: ButtonVariant;
    size?: ButtonSize;
    /** Builds the document in `palette`, the light PDF colours: clients print these. */
    docGenerator: (palette: PdfPalette) => Promise<JSX.Element>;
    fileName: string;
}
export function DownloadPDF({
    buttonText,
    docGenerator,
    fileName,
    variant = 'secondary',
    size = 'medium',
}: DownloadPdfProps) {
    const { t } = useTranslation();

    const downloadPDF = async () => {
        analytics.exportTriggered({
            exportType: 'lice_report',
            fileFormat: 'pdf',
            reportType: 'lice_report',
        });

        try {
            const { pdf } = await loadReactPdf();
            const blob = await pdf(await docGenerator(PDF_PALETTE)).toBlob();
            saveBlob(blob, `${sanitizeFileName(fileName)}.pdf`);
        } catch {
            ToastError(t('molecules.downloadPDF.failed'), 'download-pdf-error');
        }
    };

    return (
        <Button
            variant={variant}
            size={size}
            onClick={downloadPDF}
            trackingId="download_pdf"
            icon={<SvgIcon icon={Download} size="custom" customSize={20} />}>
            {buttonText}
        </Button>
    );
}
